import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { isTimeoutError } from '@/api/helpers.ts';
import { useInsuranceSearchMutation } from '@/api/hooks/mutations/use-insurance-search-mutation';
import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { logger } from '@/lib/logger';
import { allCarriersActionable } from '@/types/insurance-builder-legacy.ts';
import { reset, setVerifiedInsurances } from '@/types/insurance-builder.type.ts';
import { DiscoveryCommercialNote } from '@/views/insurance/flows/legacy/components/DiscoveryCommercialNote.tsx';
import InsuranceDiscoveryError from '@/views/insurance/flows/legacy/components/DiscoveryError.tsx';
import InsuranceDiscoveryLoading from '@/views/insurance/flows/legacy/components/DiscoveryLoading.tsx';

export default function InsuranceDiscoveryPending() {
  const navigate = useNavigate();
  const insuranceSearchMutation = useInsuranceSearchMutation();
  const {
    insuranceZipCode,
    insuranceBuilder: builder,
    patientFirstName,
    patientLastName,
    patientDateOfBirth,
    patientGender,
    patientRecipient,
  } = useSessionStore();
  const [runNow, setRunNow] = useState(false);

  const discoverInsurance = () => {
    // Validate required patient fields
    const hasValidPatientData =
      patientFirstName &&
      patientLastName &&
      patientDateOfBirth &&
      patientGender &&
      patientRecipient;

    if (!hasValidPatientData) {
      logger.error(
        new Error(
          'Insurance Discovery Pending - patient data is not valid for discovering insurance'
        )
      );

      toast.dismiss();
      toast.error('We need to double check your personal information.', {
        action: {
          label: 'Continue',
          onClick: () => {
            navigate({ to: '/1', replace: true });
          },
        },
        duration: Infinity,
      });

      // TODO Redirect to a failure state instead of just toasting
      return;
    }

    const patientData = {
      recipient: patientRecipient as 'me' | 'someone-else',
      firstName: patientFirstName,
      lastName: patientLastName,
      dateOfBirth: patientDateOfBirth,
      gender: patientGender as 'male' | 'female',
    };

    const resetBuilder = reset(builder);
    useSessionStore.getState().updateState({ insuranceBuilder: resetBuilder });

    insuranceSearchMutation.mutate(
      { ...patientData, zipCode: insuranceZipCode },
      {
        onSuccess: (discoveredInsurances) => {
          const updatedBuilder = setVerifiedInsurances(resetBuilder, ...discoveredInsurances);

          useSessionStore
            .getState()
            .updateState({ insuranceBuilder: { ...updatedBuilder, ranDiscovery: true } });

          const readyForReview = allCarriersActionable(updatedBuilder);

          if (readyForReview) navigate({ to: '/insurance/finalize', replace: true });
          else navigate({ to: '/insurance/options', replace: true });
        },
        onError: (error) => {
          if (isTimeoutError(error)) {
            logger.error('Insurance search timed out, treating as no results found', error);
          } else {
            logger.error('Failed to search for insurance', error);
          }

          useSessionStore
            .getState()
            .updateState({ insuranceBuilder: { ...resetBuilder, ranDiscovery: true } });
          navigate({ to: '/insurance/options', replace: true });
        },
      }
    );
  };

  useEffect(
    function discoverInsuranceOnMount() {
      if (runNow || !builder.hasPrivateInsurance) {
        discoverInsurance();
      }
    },
    // https://github.com/TanStack/query/issues/5341#issuecomment-3381479893
    // this is a workaround to ensure the mutation is executed immediately upon mount
    // oxlint-disable-next-line exhaustive-deps
    [runNow, builder.hasPrivateInsurance]
  );

  const retry = () => {
    insuranceSearchMutation.reset();
    discoverInsurance();
  };

  const toPatientInfo = () => navigate({ to: '/1', replace: true });

  return (
    <section>
      <NavigationBar onBack={toPatientInfo} backLabel="Patient info" stepIndicator="2 of 4" />
      <StepperCard currentStep={2} totalSteps={4}>
        {builder.hasPrivateInsurance && !runNow && (
          <DiscoveryCommercialNote onAccept={() => setRunNow(true)} />
        )}

        {insuranceSearchMutation.isPending && <InsuranceDiscoveryLoading />}
        {insuranceSearchMutation.isError && <InsuranceDiscoveryError onRetry={retry} />}
      </StepperCard>
    </section>
  );
}
